import IPPageNumber from "app/pages/InteractiveProposal/InteractiveProposalDownload/IPPageNuber";
import HexagonImg from "app/pages/InteractiveProposal/images/hexagonImg.png";
import { getSessionStore } from "app/utils/sessionStore";
import { fundingSDOptions, getThemeColors, getUSANumberFormat } from "app/utils/utils";
import { getColorVariations } from "app/utils/colorUtils";
import { Chart } from "primereact/chart";
import { barGraphOptionsFunding } from "app/uicomponents/macrocomponents/PlansSummary/config";

interface FundingRow {
  planYear: number;
  actualClientPremium: number;
  actualLenderPremium: number;
  actualInterestPayment: number;
  actualPlanFee: number;
  invoiceStatus: {
    planFee: string | null;
    interest: string | null;
    clientPremium: string | null;
    lenderPremium: string | null;
  };
}

interface IRCFundingPageProps {
  funding: FundingRow[];
  pageNumber: string | number;
}

const tableHeaders = [
  { label: "Plan Year" },
  { label: "Client Premium" },
  { label: "Lender Premium" },
  { label: "Interest Payment" },
  { label: "Plan Fee" },
  { label: "Client Status" },
  { label: "Interest Status" },
  { label: "Plan Fee Status" },
];

const statusStyle = (s: string | null): React.CSSProperties => {
  if (s === "PAID") return { color: "#166534", fontWeight: 600 };
  if (s === "PENDING") return { color: "#92400e" };
  return { color: "#6b7280" };
};

const IRCFundingPage = ({ funding, pageNumber }: IRCFundingPageProps) => {
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor, ipTooltipColor, secondaryColor } = getThemeColors(branding);

  const limitedData = funding.slice(0, 10);

  const datasets = [
    {
      backgroundColor: fundingSDOptions(primaryColor)[0].indicatorColor,
      data: limitedData.map((r) => r.actualClientPremium),
      label: "Client Premium",
    },
    {
      backgroundColor: fundingSDOptions(primaryColor)[1].indicatorColor,
      data: limitedData.map((r) => r.actualLenderPremium),
      label: "Lender Premium",
    },
    {
      backgroundColor: fundingSDOptions(primaryColor)[2].indicatorColor,
      data: limitedData.map((r) => r.actualInterestPayment),
      label: "Interest",
    },
    {
      backgroundColor: fundingSDOptions(primaryColor)[3].indicatorColor,
      data: limitedData.map((r) => r.actualPlanFee),
      label: "Plan Fee",
    },
  ];

  const colorVariations = getColorVariations(secondaryColor, tableHeaders.length).reverse();

  return (
    <div className="flex flex-col h-full relative min-h-screen">
      <div className="relative z-20 px-[2em] w-full max-w-[56em] mx-auto p-[1.75em]">
        <h1 className="text-center text-2xl font-bold mb-5 conthrax-font interactive-proposal-text">
          Plan Funding
        </h1>

        {/* Bar chart */}
        <div style={{ width: "503px" }}>
          <Chart
            type="bar"
            data={{
              labels: limitedData.map((r) => r.planYear),
              datasets,
            }}
            options={barGraphOptionsFunding(false, ipTooltipColor, "Plan Year", "", "Plan Year")}
            width="503px"
            height="220px"
          />
          <div className="flex flex-wrap justify-center gap-3 mb-3">
            {datasets.map(({ backgroundColor, label }, i) => (
              <div key={i} className="flex items-center gap-1">
                <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor }} />
                <span style={{ color: "#000000", fontSize: "12px" }}>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Funding table */}
        <table className="w-full border-collapse">
          <thead className="text-white">
            <tr>
              {tableHeaders.map((h, i) => (
                <th
                  key={i}
                  className="p-1 text-center pdf_table_border border-white font-semibold"
                  style={{ backgroundColor: colorVariations[i], fontSize: "9px" }}
                >
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {funding.map((row, i) => (
              <tr key={i} style={{ backgroundColor: "#EEEEEE", color: "#000000", fontSize: "9px" }}>
                <td className="p-1 text-center pdf_table_border border-white">{row.planYear}</td>
                <td className="p-1 text-center pdf_table_border border-white">
                  ${getUSANumberFormat(row.actualClientPremium, false)}
                </td>
                <td className="p-1 text-center pdf_table_border border-white">
                  ${getUSANumberFormat(row.actualLenderPremium, false)}
                </td>
                <td className="p-1 text-center pdf_table_border border-white">
                  ${getUSANumberFormat(row.actualInterestPayment, false)}
                </td>
                <td className="p-1 text-center pdf_table_border border-white">
                  ${getUSANumberFormat(row.actualPlanFee, false)}
                </td>
                <td
                  className="p-1 text-center pdf_table_border border-white"
                  style={statusStyle(row.invoiceStatus?.clientPremium)}
                >
                  {row.invoiceStatus?.clientPremium ?? "—"}
                </td>
                <td
                  className="p-1 text-center pdf_table_border border-white"
                  style={statusStyle(row.invoiceStatus?.interest)}
                >
                  {row.invoiceStatus?.interest ?? "—"}
                </td>
                <td
                  className="p-1 text-center pdf_table_border border-white"
                  style={statusStyle(row.invoiceStatus?.planFee)}
                >
                  {row.invoiceStatus?.planFee ?? "—"}
                </td>
              </tr>
            ))}
            {/* Totals row */}
            <tr
              style={{
                backgroundColor: "#D1D5DB",
                color: "#000000",
                fontSize: "9px",
                fontWeight: 600,
              }}
            >
              <td className="p-1 text-center pdf_table_border border-white">Total</td>
              <td className="p-1 text-center pdf_table_border border-white">
                $
                {getUSANumberFormat(
                  funding.reduce((s, r) => s + r.actualClientPremium, 0),
                  false,
                )}
              </td>
              <td className="p-1 text-center pdf_table_border border-white">
                $
                {getUSANumberFormat(
                  funding.reduce((s, r) => s + r.actualLenderPremium, 0),
                  false,
                )}
              </td>
              <td className="p-1 text-center pdf_table_border border-white">
                $
                {getUSANumberFormat(
                  funding.reduce((s, r) => s + r.actualInterestPayment, 0),
                  false,
                )}
              </td>
              <td className="p-1 text-center pdf_table_border border-white">
                $
                {getUSANumberFormat(
                  funding.reduce((s, r) => s + r.actualPlanFee, 0),
                  false,
                )}
              </td>
              <td className="p-1 pdf_table_border border-white" colSpan={3} />
            </tr>
          </tbody>
        </table>
      </div>

      <div className="absolute bottom-0 left-0 w-full">
        <div
          className="h-[350px] bg-cover bg-center"
          style={{ backgroundImage: `url(${HexagonImg})` }}
        />
        <div className="px-[4em] w-full max-w-[56em] mx-auto p-[1.75em] pt-1">
          <IPPageNumber number={pageNumber} />
        </div>
      </div>
    </div>
  );
};

export default IRCFundingPage;
